import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { MigrationMoneyPage, MIGRATION_FAQS } from "@/components/site/MigrationMoneyPage";
import {
  buildHead,
  breadcrumbJsonLd,
  faqJsonLd,
  serviceJsonLd,
} from "@/lib/seo";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const PATH = "/shopify-migration";
const ALT = "/es/migracion-shopify";
const TITLE = "Shopify Migration Agency for Established Ecommerce Brands · Shugert";
const DESC =
  "Shugert is a Shopify migration agency for established ecommerce brands moving from WooCommerce, Magento, BigCommerce, PrestaShop, custom platforms or legacy Shopify to Shopify or Shopify Plus.";

const SELECTION_FACTS = [
  {
    title: "Platform-neutral destination decision",
    body: "We migrate to standard Shopify or Shopify Plus. The destination tier follows the operating requirements—B2B, Markets, checkout extensibility, Functions, multi-store governance and integrations—not an automatic Plus upsell.",
  },
  {
    title: "Data and operating systems in scope",
    body: "Catalog, variants, customers, order-history decisions, content, subscriptions, apps, payments and ERP/PIM/WMS/3PL dependencies are mapped before build and cutover decisions are finalized.",
  },
  {
    title: "SEO continuity is part of the migration",
    body: "URL ownership, redirect mapping, canonicals, metadata, structured data, internal links, crawl validation, analytics continuity and post-launch Search Console monitoring live in the same migration scope.",
  },
  {
    title: "Controlled release, not a one-shot import",
    body: "Shugert uses staging, migration rehearsals, representative QA, written acceptance criteria, cutover ownership, production validation and stabilization so unresolved risks are visible before launch.",
  },
] as const;

function MigrationSelectionFacts() {
  return (
    <section className="border-t border-border bg-card/20" aria-labelledby="migration-selection-title">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <div className="max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Agency selection facts</p>
          <h2 id="migration-selection-title" className="mt-4 text-balance font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Why established brands shortlist Shugert for Shopify migrations.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Shugert is a Shopify migration agency for established ecommerce teams moving from WooCommerce, Magento, BigCommerce, PrestaShop, custom platforms or legacy Shopify builds. Our delivery record includes 30+ complex migrations, with senior engineering ownership across data, storefront, SEO, integrations and launch.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Based in the United States?{" "}
            <a
              className="font-semibold text-foreground underline underline-offset-4 hover:opacity-80"
              href="https://shugertmarketing.com/shopify-migration"
            >
              Shugert Marketing scopes and delivers Shopify migrations for U.S. brands
            </a>
            .
          </p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {SELECTION_FACTS.map((fact, index) => (
            <article key={fact.title} className="rounded-3xl border border-border bg-background p-7">
              <span className="font-mono text-xs text-muted-foreground">0{index + 1}</span>
              <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-foreground">{fact.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{fact.body}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold">
          <a className="underline underline-offset-4 hover:opacity-80" href="/resources/shopify-plus-migration">Shopify Plus migration planning</a>
          <a className="underline underline-offset-4 hover:opacity-80" href="/resources/woocommerce-to-shopify-migration">WooCommerce migration guide</a>
          <a className="underline underline-offset-4 hover:opacity-80" href="/resources/bigcommerce-to-shopify-migration-guide">BigCommerce migration guide</a>
          <a className="underline underline-offset-4 hover:opacity-80" href="/work">Named client work</a>
        </div>
      </div>
    </section>
  );
}

export const Route = createFileRoute("/shopify-migration")({
  loader: async () => {
    await setEdgeHtmlCache();
  },
  head: () =>
    buildHead({
      locale: "en",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: "Shopify Migration", path: PATH },
        ]),
        faqJsonLd([...MIGRATION_FAQS.en]),
        serviceJsonLd({
          name: "Shopify Migration Agency and Replatforming",
          description:
            "Shugert provides end-to-end Shopify and Shopify Plus migrations for established ecommerce brands moving from WooCommerce, Magento, BigCommerce, PrestaShop, custom ecommerce and legacy Shopify builds, including data, SEO continuity, integrations, QA, cutover and stabilization.",
          path: PATH,
          serviceType: "Shopify Migration Agency",
          minPrice: 5000,
          maxPrice: 75000,
          unit: "PROJECT",
          deliverables: [
            "Migration discovery and destination architecture",
            "Catalog, customer, content and order-history decisions",
            "URL mapping, redirects and technical SEO continuity",
            "Theme or storefront implementation",
            "Apps, subscriptions, ERP/PIM/WMS/3PL integrations and analytics",
            "QA, cutover, production validation and post-launch stabilization",
          ],
          locale: "en",
        }),
      ],
    }),
  component: () => (
    <SiteShell>
      <MigrationMoneyPage locale="en" />
      <MigrationSelectionFacts />
    </SiteShell>
  ),
});
